import React, {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useClassName } from 'common/hooks';
import './parks-and-attractions.less';
import {
  getContentfulAssetUrlandAlt,
  getContentfulField,
  unwrapContentfulFields,
} from 'common/components';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Button } from 'common/lazy';
import Card from 'public/screens/CustomLandingPage/Components/Card';

// Lazy-loaded to break circular dependency: components -> ParkScreen -> ParksAndAttractions -> CustomLandingPage -> components
const Section = lazy(() => import('public/screens/CustomLandingPage/Section'));

export const ParksAndAttractions = ({ venue }) => {
  const className = useClassName('ParksAndAttractions');
  //const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const secondaryColor = getContentfulField(venue.parkScreen?.secondaryColor);
  const mainColor = getContentfulField(venue.parkScreen?.mainColor);
  const tertiaryColor = getContentfulField(venue.parkScreen?.tertiaryColor);
  const slidesAttractions = getContentfulField(
    venue.parkScreen?.slidesAttractions
  );

  const settingsList = getContentfulField(slidesAttractions.fields.settings);
  const descriptionValue = settingsList?.find(
    (setting) => getContentfulField(setting.fields.key) === 'description'
  )?.fields?.richTextValue;
  const description = descriptionValue
    ? getContentfulField(descriptionValue)
    : null;
  const name = slidesAttractions.fields.name
    ? getContentfulField(slidesAttractions.fields.name)
    : null;
  // Process items if they exist
  const itemsElements = getContentfulField(slidesAttractions.fields.items);
  const items = itemsElements
    .filter((item) => item.fields.asset)
    .map((item) => {
      const assetItem = getContentfulField(item.fields.asset).fields;

      const asset = {
        desktop: getContentfulAssetUrlandAlt(assetItem.desktop),
        tablet: getContentfulAssetUrlandAlt(assetItem.tablet),
        mobile: getContentfulAssetUrlandAlt(assetItem.mobile),
      };

      const links = getContentfulField(item.fields.links);
      const link =
        links && links.length > 0
          ? {
              label: getContentfulField(links[0].fields.label),
              url: getContentfulField(links[0].fields.url),
              type: getContentfulField(links[0].fields.type),
            }
          : null;

      const settingsList = item.fields.settings
        ? getContentfulField(item.fields.settings)
        : null;
      const types = settingsList
        ? settingsList.filter(
            (setting) => getContentfulField(setting.fields.key) === 'type'
          )
        : [];

      return {
        raw: item,
        title: getContentfulField(item.fields.title),
        asset,
        link,
        types: types.map((t) => getContentfulField(t.fields?.value)),
      };
    });

  const [selectedFilter, setSelectedFilter] = useState(null);
  //const [selectedMoreFilters, setSelectedMoreFilters] = useState([]);
  const handleFilterClick = (value) => {
    setSelectedFilter(value);
  };

  // const handleMoreFilterClick = () => {
  //   setShowMoreFilters(!showMoreFilters);
  // };

  const uniqueTypes = [...new Set(items.flatMap((item) => item.types))].filter(
    Boolean
  );
  console.log(uniqueTypes);

  const filters = [
    {
      label: 'All',
      value: null,
    },
    ...uniqueTypes.map((type) => ({
      label: type
        ? type
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        : null,
      value: type ? type : null,
    })),
  ];

  // const moreFilters = [
  //   {
  //     value: 'suitable_for_pregnant_women',
  //     label: 'Suitable for pregnant women',
  //   },
  //   {
  //     value: 'wheelchair_accessible',
  //     label: 'Wheelchair accessible',
  //   },
  //   {
  //     value: 'supervising_companion_needed',
  //     label: 'Supervising companion needed',
  //   },
  //   {
  //     value: 'under_manteinance',
  //     label: 'Under manteinance',
  //   },
  // ];

  const allFilteredItems = useMemo(() => {
    return items.filter((item) => {
      return selectedFilter === null || item.types.includes(selectedFilter);
    });
  }, [items, selectedFilter]);

  const filteredItems =
    selectedFilter !== null || showAll
      ? allFilteredItems
      : allFilteredItems.slice(0, 6);

  const shouldShowLoadMore =
    selectedFilter === null && !showAll && items.length > 6;

  const videoRefs = useMemo(() => {
    const refs = {};
    items.forEach((item) => {
      refs[item.title] = React.createRef();
    });
    return refs;
  }, [items]);

  useEffect(() => {
    const checkVideoVisibility = () => {
      const visibleVideos = filteredItems
        .map((item) => {
          const videoRef = videoRefs[item.title];
          if (!videoRef?.current) return null;

          const rect = videoRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight;

          // the video is completely visible if:
          // - its top part is inside the viewport (not out by above)
          // - its bottom part is inside the viewport (not out by below)
          const isCompletelyVisible =
            rect.top >= 0 && rect.bottom <= viewportHeight;

          return isCompletelyVisible ? { item, videoRef } : null;
        })
        .filter(Boolean);

      const videoToPlay =
        visibleVideos.length > 0 ? visibleVideos[0].videoRef : null;

      // pause all videos except the one that should be playing
      Object.values(videoRefs).forEach((videoRef) => {
        if (videoRef?.current && videoRef !== videoToPlay) {
          if (!videoRef.current.paused) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
          }
        }
      });

      // play the first visible video only if it's not already playing
      if (videoToPlay?.current) {
        if (videoToPlay.current.paused) {
          videoToPlay.current.play().catch((error) => {
            console.error('Error playing video:', error);
          });
        }
      }
    };

    checkVideoVisibility();
    window.addEventListener('scroll', checkVideoVisibility, { passive: true });

    return () => {
      window.removeEventListener('scroll', checkVideoVisibility);
    };
  }, [filteredItems, videoRefs]);

  const moreActivitiesRaw = getContentfulField(
    venue.parkScreen?.moreActivities
  );
  const moreActivities = moreActivitiesRaw
    ? unwrapContentfulFields(moreActivitiesRaw)
    : null;
  const refs = useRef([]);

  return (
    <div
      id="attractions"
      className={className('container')}
      style={{
        background: `linear-gradient(180deg, ${secondaryColor} 0%, ${secondaryColor} 49%, ${tertiaryColor} 50%, ${tertiaryColor} 100%)`,
      }}>
      <div
        className={className('container__wrapper')}
        style={{ backgroundColor: mainColor }}>
        <div className={className('container__inner')}>
          <div className={className('container__header')}>
            <h2 className={className('container__header__title')}>{name}</h2>
            <p className={className('container__header__description')}>
              {documentToReactComponents(description)}
            </p>
            <div className={className('container__header__filters')}>
              {filters.map((filter) => (
                <Button
                  key={filter.value}
                  onClick={() => handleFilterClick(filter.value)}
                  className={className(
                    'container__header__filters__item',
                    selectedFilter === filter.value ? 'selected' : null
                  )}
                  content={filter.label}
                />
              ))}
              {/* <Button
                onClick={() => handleMoreFilterClick()}
                className={className('container__header__filters__item')}
                content={
                  <div
                    className={className(
                      'container__header__filters__item__more',
                      selectedMoreFilters.length > 0 ? 'with-filters' : null
                    )}>
                    <Icon name="sliders" size="small" />
                    <span>More filters</span>
                  </div>
                }
              />
              <MoreFiltersModal
                visible={showMoreFilters}
                onClose={() => setShowMoreFilters(false)}
                onSave={(filters) => {
                  setSelectedMoreFilters(filters);
                  setShowMoreFilters(false);
                }}
                filters={moreFilters}
                selectedFilters={selectedMoreFilters}
              /> */}
            </div>
          </div>
          <div className={className('container__items')}>
            {filteredItems.map((item) => {
              const cardItem = unwrapContentfulFields(item.raw);
              return <Card key={item.title} refs={[]} item={cardItem} />;
            })}
          </div>
          {shouldShowLoadMore && (
            <div className={className('container__load-more')}>
              <Button
                content="Explore more"
                primary
                onClick={() => setShowAll(true)}
              />
            </div>
          )}
        </div>
        {moreActivities && (
          <div className={className('container__inner')}>
            <Suspense fallback={null}>
              <Section
                section={moreActivities}
                rawSection={moreActivitiesRaw}
                refs={refs}
                index={0}
              />
            </Suspense>
          </div>
        )}
      </div>
    </div>
  );
};
